import { useMemo, useState, type ReactElement } from 'react';
import { Plug, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminFhirIntegrationMutations,
  useAdminFhirIntegrations,
  type FhirIntegrationInput,
  type FhirIntegrationViewModel,
} from '@/features/admin-fhir-integrations/integrationApi';
import { AdminFhirIntegrationsTable } from '@/features/admin-fhir-integrations/components/AdminFhirIntegrationsTable';
import { DeleteFhirIntegrationConfirmDialog } from '@/features/admin-fhir-integrations/components/DeleteFhirIntegrationConfirmDialog';
import { FhirIntegrationFormDialog } from '@/features/admin-fhir-integrations/components/FhirIntegrationFormDialog';

/**
 * Admin CRUD for per-tenant FHIR integration entries (PTV2-1383). Each entry carries the launch
 * knobs, the FHIR service root URL, the encrypted system-account credentials and the Auth0 M2M
 * settings that were previously spread across the config catalog and deployment env. Mirrors the
 * admin Email Servers screen.
 */
export function AdminFhirIntegrationsListPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useAdminFhirIntegrations();
  const { create, update, remove } = useAdminFhirIntegrationMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState<FhirIntegrationViewModel | null>(null);
  const [deleteRow, setDeleteRow] = useState<FhirIntegrationViewModel | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [query.isSuccess, query.data],
  );

  const isEdit = formInitial !== null;
  const formPending = isEdit ? update.isPending : create.isPending;
  const formError = isEdit ? update.error : create.error;

  function closeForm(): void {
    setFormOpen(false);
    setFormInitial(null);
    create.reset();
    update.reset();
  }

  function handleFormSubmit(input: FhirIntegrationInput): void {
    if (formInitial !== null) {
      update.mutate({ ...input, id: formInitial.id }, { onSuccess: closeForm });
    } else {
      create.mutate(input, { onSuccess: closeForm });
    }
  }

  function handleDeleteConfirm(row: FhirIntegrationViewModel): void {
    remove.mutate(row.id, {
      onSuccess: () => {
        setDeleteRow(null);
        remove.reset();
      },
    });
  }

  const base = 'routes.admin.fhirIntegrations.entries';

  return (
    <>
      <PageHeader
        icon={<Plug className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.fhirIntegrations.heading')}
        subtitle={t('routes.admin.fhirIntegrations.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-fhir-integrations-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 id="admin-fhir-integrations-heading" className="text-base font-semibold text-foreground">
                {t(`${base}.heading`)}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t(`${base}.subheading`)}</p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                update.reset();
                setFormInitial(null);
                setFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t(`${base}.addAction`)}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t(`${base}.error.heading`)}
                message={t(`${base}.error.message`)}
                codeLabel={t(`${base}.error.codeLabel`)}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t(`${base}.error.retry`)}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t(`${base}.empty`)}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t(`${base}.heading`)}>
              <AdminFhirIntegrationsTable
                rows={rows}
                onManage={(row) => {
                  void navigate(`/admin/fhir-integrations/${encodeURIComponent(row.id)}`);
                }}
                onEdit={(row) => {
                  create.reset();
                  update.reset();
                  setFormInitial(row);
                  setFormOpen(true);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setDeleteRow(row);
                }}
                pendingDeleteId={remove.isPending && deleteRow !== null ? deleteRow.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <FhirIntegrationFormDialog
        open={formOpen}
        initial={formInitial}
        onOpenChange={(next) => {
          if (!next && formPending) return;
          if (!next) closeForm();
          else setFormOpen(true);
        }}
        onSubmit={handleFormSubmit}
        pending={formPending}
        submitError={formError}
      />

      <DeleteFhirIntegrationConfirmDialog
        row={deleteRow}
        onCancel={() => {
          if (remove.isPending) return;
          setDeleteRow(null);
          remove.reset();
        }}
        onConfirm={handleDeleteConfirm}
        pending={remove.isPending}
        submitError={remove.error}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
